import React, { useCallback, useImperativeHandle, useState } from 'react'
import PropTypes from 'prop-types'
import { TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import RoundedModal from '../../../../molecules/RoundedModal'
import SydButton from '../../../../commonDesign/Button'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: '8px 0'
  }
})

function slugify (text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

const CreateDagDialog = React.forwardRef(function CreateDagDialog ({ onConfirm }, ref) {
  const classes = useStyles()
  const [dialogState, setDialogState] = useState({
    open: false,
    processing: false,
    name: '',
    dagId: '',
    autoSlug: true
  })

  const close = useCallback(() => {
    setDialogState({ open: false, processing: false, name: '', dagId: '', autoSlug: true })
  }, [])

  const handleNameChange = useCallback((e) => {
    const name = e.target.value
    setDialogState((prev) => ({
      ...prev,
      name,
      dagId: prev.autoSlug ? slugify(name) : prev.dagId
    }))
  }, [])

  const handleDagIdChange = useCallback((e) => {
    setDialogState((prev) => ({
      ...prev,
      dagId: e.target.value,
      autoSlug: false
    }))
  }, [])

  const handleConfirm = useCallback(async () => {
    try {
      setDialogState((prev) => ({ ...prev, processing: true }))
      await onConfirm({
        dagId: dialogState.dagId,
        name: dialogState.name,
        configuration: {},
        steps: [
          { dagStepId: 'start', name: 'Start', targetAction: 'start', configuration: {} }
        ]
      })
    } catch {
      setDialogState((prev) => ({ ...prev, processing: false }))
    }
  }, [dialogState.dagId, dialogState.name, onConfirm])

  useImperativeHandle(ref, () => ({
    open: () => {
      setDialogState({ open: true, processing: false, name: '', dagId: '', autoSlug: true })
    }
  }), [])

  const canCreate = dialogState.name.trim().length > 0 && dialogState.dagId.trim().length > 0

  return (
    <RoundedModal
      title='New Workflow'
      open={dialogState.open}
      onClose={close}
      size='medium'
      position='top'
      disablePortal={false}
    >
      <div>
        {dialogState.open ? (
          <>
            <div className={classes.form}>
              <TextField
                label='Workflow Name'
                variant='outlined'
                size='small'
                value={dialogState.name}
                onChange={handleNameChange}
                autoFocus
                fullWidth
              />
              <TextField
                label='DAG ID'
                variant='outlined'
                size='small'
                value={dialogState.dagId}
                onChange={handleDagIdChange}
                helperText='Unique identifier (auto-generated from name, or enter your own)'
                fullWidth
              />
            </div>
            <SydModalActions>
              <SydButton disabled={dialogState.processing} variant='ghost' size='md' onClick={close}>
                Cancel
              </SydButton>
              <SydButton
                processing={dialogState.processing}
                disabled={!canCreate}
                variant='primary'
                icon='add'
                size='md'
                onClick={handleConfirm}
              >
                Create
              </SydButton>
            </SydModalActions>
          </>
        ) : null}
      </div>
    </RoundedModal>
  )
})

CreateDagDialog.propTypes = {
  onConfirm: PropTypes.func.isRequired
}

export default CreateDagDialog
